import { Button, Divider, Grid, Tooltip, Typography } from '@mui/material';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { FiberManualRecordOutlined } from '@mui/icons-material';
import Transition from '../common/Transition';
import moment from 'moment';
import { CancelOutlined, CheckCircleOutline } from '@mui/icons-material';
import { Header } from '../common/Header';
import { GetOptimizationDetails, handleToggleOptimizationDetailsDialog } from '../../store/optimization-slice/optimization';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const OptimizationDetailsDialog = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { 
        optimizationDetailsLoading,
        optimizationDetails, 
        optimizationDetailsDialog
    } = useAppSelector((state: RootState) => state.optimization);

    function handleClose() {
        dispatch(handleToggleOptimizationDetailsDialog({ isOpen: false, body: null }))
    }

    useEffect(() => {
        if(optimizationDetailsDialog && optimizationDetailsDialog.isOpen)
            dispatch(GetOptimizationDetails(optimizationDetailsDialog?.body?.id));
    }, [optimizationDetailsDialog])

    return(
        <Dialog
            fullWidth
            maxWidth="lg"
            open={optimizationDetailsDialog.isOpen}
            TransitionComponent={Transition}
            keepMounted
            onClose={(_, reason) => {
                if(reason !== 'backdropClick') handleClose();
            }}
            aria-describedby="alert-dialog-slide-description"
        >
            <Header title={t('OPTIMIZATION_DETAILS_TITLE', { name: optimizationDetailsDialog?.body?.name })} />
            <DialogContent>
                { !optimizationDetails || optimizationDetailsLoading ? 
                <Grid container p={2} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    { optimizationDetailsLoading ? 
                        <Box sx={{ width: '100%' }}>
                            <LinearProgress />
                        </Box>
                    : !optimizationDetails ? 
                        <Typography variant={'h6'}>{t('NO_OPTIMIZATION_DETAILS')}</Typography>
                    : '' }
                </Grid> : 
                <React.Fragment>
                    <Grid container sx={{ display: 'flex', flexDirection: 'row'}} spacing={10}>
                        <Grid item xs={6}>
                            <Grid mb={1} item style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                <HelpOutlineIcon />
                                <Typography fontWeight="bold" ml={1} variant='h6'>{t('OPTIMIZATION_DETAILS_REQUEST')}</Typography>
                            </Grid>
                            <Divider />
                            <Grid mt={1.5} item xs={12}>
                                <Grid container display={"flex"} justifyContent={"space-between"}>
                                    <Grid item fontWeight="bold">{t('OPTIMIZATION_RESULT_NAME')}</Grid>
                                    <Grid item>{optimizationDetails?.request?.name ?? '---'}</Grid>
                                </Grid>
                            </Grid>
                            <Grid mt={1} item><Divider /></Grid>
                            <Grid item>
                                <Grid mt={1.5} container display={"flex"} justifyContent={"space-between"}>
                                    <Grid item fontWeight="bold">{t('OPTIMIZATION_MAXIMISE_OUTPUT')}</Grid>
                                    <Grid item>{optimizationDetails?.request?.maximiseOutput ? <CheckCircleOutline sx={{ color: "green" }}/> : <CancelOutlined sx={{ color: 'red' }}/>}</Grid>
                                </Grid>
                            </Grid>
                            <Grid item mt={.5} xs={12}><Divider /></Grid>
                            <Grid container mt={1.5} display={"flex"} flexDirection={"column"}>
                                <Grid item xs={12}>
                                    <Grid item xs={12} fontWeight="bold">
                                        {t('OPTIMIZATION_INPUTS')}
                                    </Grid>
                                    <Grid item xs={12} sx={{ height: '199px', overflowY: 'auto' }}>
                                        { optimizationDetails?.request?.inputColumns?.map((el : any, index: number) => {
                                            return(
                                                <Grid key={index} item xs={12} pr={1}>
                                                    <Grid container display={"flex"} justifyContent={"space-between"}>
                                                        <Grid item sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'row'}}>
                                                            <FiberManualRecordOutlined fontSize='small' />
                                                            <Typography ml={.5}>
                                                                {el.name} → {el.value}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            )
                                        })}
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item mt={1.5} xs={12}><Divider /></Grid>
                            <Grid 
                                container 
                                mt={1.5} 
                                display={"flex"} 
                                flexDirection={"column"}
                                sx={
                                    !optimizationDetails?.request?.varyingColumns || optimizationDetails?.request?.varyingColumns?.length === 0 ? { display: 'none' } : {}
                                }
                            >
                                <Grid item xs={12}>
                                    <Grid item xs={12} fontWeight="bold">
                                        {t('OPTIMIZATION_OPTIMIZED_COLUMNS')}
                                    </Grid>
                                    <Grid item xs={12} sx={{ height: '199px', overflowY: 'auto' }}>
                                        { optimizationDetails?.request?.varyingColumns?.map((el : any, index: number) => {
                                            return(
                                                <Grid key={index} item xs={12} pr={1}>
                                                    <Grid container display={"flex"} justifyContent={"space-between"}>
                                                        <Grid item sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'row'}}>
                                                            <FiberManualRecordOutlined fontSize='small' />
                                                            <Typography ml={.5}>{el}</Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            )
                                        })}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={6}>
                            <Grid mb={1} container display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
                                <Grid item display="flex" flexDirection="row" alignItems="center">
                                    <QuestionAnswerIcon />
                                    <Typography fontWeight="bold" ml={1} variant='h6'>{t('OPTIMIZATION_DETAILS_RESPONSE')}</Typography>
                                </Grid>
                                <Tooltip title={
                                    <React.Fragment>
                                        <Grid container display={"flex"} justifyContent={"space-between"} spacing={1}>
                                            <Grid item fontWeight="bold">{t('OPTIMIZATION_DETAILS_START_DATE_TIME')}</Grid>
                                            <Grid item>{moment(optimizationDetails?.response?.startOptimiseDateTimeUTC).format('DD/MM/YYYY hh:mm')}</Grid>
                                        </Grid>
                                        <Grid container display={"flex"} justifyContent={"space-between"} spacing={1}>
                                            <Grid item fontWeight="bold">{t('OPTIMIZATION_DETAILS_END_DATE_TIME')}</Grid>
                                            <Grid item>{moment(optimizationDetails?.response?.endOptimiseDateTimeUTC).format('DD/MM/YYYY hh:mm')}</Grid>
                                        </Grid>
                                        <Grid container display={"flex"} justifyContent={"space-between"} spacing={1}>
                                            <Grid item fontWeight="bold">{t('OPTIMIZATION_DETAILS_DURATION')}</Grid>
                                            <Grid item>{optimizationDetails?.response?.optimiseDuration}</Grid>
                                        </Grid>
                                    </React.Fragment>
                                }>
                                    <Grid item display="flex" flexDirection="row" alignItems="center">
                                        <AccessTimeIcon />
                                        <Typography ml={1}>{optimizationDetails?.response?.optimiseDuration}</Typography>
                                    </Grid>
                                </Tooltip>
                            </Grid>
                            <Grid container display={"flex"} flexDirection={"column"} spacing={1}>
                                <Grid item xs={12}><Divider /></Grid>
                                <Grid item xs={12} fontWeight="bold">
                                    {t('OPTIMIZATION_RESULTS')}
                                </Grid>
                                <Grid item xs={12} sx={{ height: '400px', overflowY: 'auto' }}>
                                    <Typography variant="body1" fontWeight="bold">
                                        {t(
                                            optimizationDetails?.response?.optimisationResult?.maximiseOutput
                                            ? 'OPTIMIZATION_RESULT_MAXIMIZE_HIGHEST_TITLE'
                                            : 'OPTIMIZATION_RESULT_MAXIMIZE_LOWEST_TITLE'
                                        )}
                                    </Typography>
                                    { optimizationDetails?.response?.optimisationResult?.bestOptions?.map((el: { outputText: string, varyingText: string[] }, index: number) => {
                                        return(
                                            <Grid
                                                key={index}
                                                item
                                                xs={12}
                                                mb={1}
                                                p={1}
                                                sx={{
                                                    border: '0.5px solid gray',
                                                    borderRadius: '4px',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                    backgroundColor: '#f9f9f9',
                                                }}
                                            >
                                                <Grid container flexDirection="column">
                                                    <Grid
                                                        item
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            flexDirection: 'row',
                                                            paddingBottom: 1,
                                                            borderBottom: '0.5px solid lightgray',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{ fontWeight: 'bold', color: '#333', marginRight: 1 }}
                                                        >
                                                            {index + 1}.
                                                        </Typography>
                                                        <Typography>{t('OPTIMIZATION_RESULT_RESULT_TEXT', { output: el.outputText })}</Typography>
                                                    </Grid>
                                                    <Grid item sx={{ paddingTop: 1 }}>
                                                        {el.varyingText?.map((text: string, idx: number) => (
                                                            <Typography key={idx} variant="body2" sx={{ color: '#555' }}>
                                                                {text}
                                                            </Typography>
                                                        ))}
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        )
                                    })}
                                    {
                                        optimizationDetails?.response?.optimisationResult?.otherOptions && optimizationDetails?.response?.optimisationResult?.otherOptions.length != 0 ?
                                        <Typography 
                                            fontSize="15px" 
                                            mt={1.5} 
                                            sx={{ color: '#666' }}
                                        >
                                            {t('OPTIMIZATION_RESULT_OTHER_OPTIONS')}
                                        </Typography> : '' 
                                    }
                                { optimizationDetails?.response?.optimisationResult?.otherOptions?.map(
                                    (el: { outputText: string; varyingText: string[] }, index: number) => {
                                        return (
                                            <Grid
                                                key={index}
                                                item
                                                xs={12}
                                                pr={1}
                                                mb={1}
                                                sx={{
                                                    border: '0.5px dashed lightgray',
                                                    borderRadius: '4px',
                                                    backgroundColor: '#f0f0f0',
                                                    padding: '8px',
                                                }}
                                            >
                                                <Grid container flexDirection="column">
                                                    <Grid
                                                        item
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            flexDirection: 'row',
                                                            color: '#888',
                                                        }}
                                                    >
                                                        <Typography fontSize="14px" sx={{ fontWeight: 'medium' }}>
                                                            {index + 1}. &nbsp;
                                                        </Typography>
                                                        <Typography fontSize="14px">
                                                            {t('OPTIMIZATION_RESULT_RESULT_TEXT', { output: el.outputText })}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item mt={1}>
                                                        {el.varyingText?.map((text: string, idx: number) => (
                                                            <Typography key={idx} fontSize="14px" sx={{ color: '#555' }}>
                                                            {text}
                                                            </Typography>
                                                        ))}
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        );
                                        }
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </React.Fragment>
                }
            </DialogContent>
            <DialogActions>
                <Button className="button-text-color" onClick={handleClose}>{t('CLOSE_BUTTON')}</Button>
            </DialogActions> 
        </Dialog>
    )
}

export default OptimizationDetailsDialog;